import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import {
  useSpedSettingsByCompanyId,
  useSpedSettingsCreate,
} from '@/core/domain/sped/sped-settings/sped-settings.hooks';
import { SpedSettings } from '@/core/domain/sped/sped-settings/sped-settings.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Alert, Button, Card, Form } from 'antd';
import { useCallback, useEffect } from 'react';

interface Props {
  spedSettings?: SpedSettings;
  onSettingsSelected: (spedSettings?: SpedSettings) => void;
}

export default function SpedSettingsCompany(props: Props) {
  const [form] = Form.useForm<{ companyId: number }>();

  const companyId = Form.useWatch('companyId', form);

  const spedSettingsByCompany = useSpedSettingsByCompanyId(companyId);
  const spedSettingsCreate = useSpedSettingsCreate();

  const spedSettingsNotFound =
    spedSettingsByCompany.isFetched && !spedSettingsByCompany.data;

  useEffect(() => {
    if (props.spedSettings?.companyId === companyId) return;

    const spedSettings = spedSettingsByCompany.data;

    if (spedSettings) {
      props.onSettingsSelected(spedSettings);
    } else {
      props.onSettingsSelected(undefined);
    }
  }, [companyId, spedSettingsByCompany.dataUpdatedAt, props.spedSettings]);

  const createSpedSettings = useCallback(async () => {
    await spedSettingsCreate.mutateAsync(companyId);
    await spedSettingsByCompany.refetch();
  }, [companyId]);

  if (props.spedSettings) {
    return (
      <Card style={{ borderRadius: 8 }}>
        <Form
          form={form}
          initialValues={{ companyId: props.spedSettings.companyId }}
        >
          <strong>Empresa selecionada</strong>
          <FloatingFormItem
            name="companyId"
            label="Empresa"
            style={{ marginTop: 16 }}
          >
            <CompaniesInfiniteSelectField
              queryEnable
              queryFilter={{ filterActiveSped: true }}
            />
          </FloatingFormItem>
        </Form>
      </Card>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        maxWidth: 500,
        padding: '48px 5px',
        gap: 24,
        position: 'relative',
      }}
    >
      <Form form={form}>
        <h3>
          Selecione a empresa com o SPED ativo para criar/visualizar a
          configuração do SPED
        </h3>

        <FloatingFormItem
          name="companyId"
          label="Empresa"
          style={{ marginTop: 24 }}
        >
          <CompaniesInfiniteSelectField
            queryFilter={{ filterActiveSped: true }}
          />
        </FloatingFormItem>
      </Form>

      {spedSettingsByCompany.isLoading && (
        <ModalLoadingOverlay
          posAbsolute
          messages={['Consultando configuração sped da empresa...']}
        />
      )}

      {spedSettingsNotFound && (
        <Alert
          type="warning"
          description={
            <>
              <p>
                Não existe uma configuração SPED para essa empresa deseja
                cadastrar?
              </p>
              <Button
                onClick={createSpedSettings}
                loading={spedSettingsCreate.isLoading}
                disabled={!hasPermissions([Authorities.SPED_CONFIG_ADD])}
              >
                Criar configuração
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}
